import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Business as SuppliersIcon,
  People as ClientsIcon,
  RequestQuote as QuoteIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  Add as AddIcon,
  List as ListIcon,
  Drafts as DraftsIcon,
  AccountBalanceWallet as ContabilidadIcon,
  ReceiptLong as ComprobanteEgresoIcon,
  PointOfSale as ReciboCajaIcon,
  AccountBalance as InformeBancarioIcon,
  CollectionsBookmark as CatalogoIcon,
  Link as MatchingIcon,
} from '@mui/icons-material';
import { Logo } from '../Logo/Logo';
import { APP_VERSION } from '../../../../../config/version';

interface SideMenuProps {
  onClose: () => void;
  onMenuClick?: (menuPath: string) => void;
}

interface SubMenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  description: string;
  path?: string;
  subItems?: SubMenuItem[];
}

const menuItems: MenuItem[] = [
  {
    text: 'Catálogo',
    icon: <CatalogoIcon />,
    description: 'Catálogo unificado de productos',
    subItems: [
      {
        text: 'Buscar productos',
        icon: <ListIcon />,
        path: '/catalogo',
      },
      {
        text: 'Pendientes de matching',
        icon: <MatchingIcon />,
        path: '/catalogo/matching',
      },
    ],
  },
  {
    text: 'Proveedores',
    icon: <SuppliersIcon />,
    description: 'Gestión de proveedores',
    path: '/proveedores/admin',
  },
  {
    text: 'Clientes',
    icon: <ClientsIcon />,
    description: 'Gestión de clientes',
    subItems: [
      {
        text: 'Listar',
        icon: <ListIcon />,
        path: '/clientes',
      },
    ],
  },
  {
    text: 'Cotización',
    icon: <QuoteIcon />,
    description: 'Sistema de cotizaciones',
    subItems: [
      {
        text: 'Crear Cotización',
        icon: <AddIcon />,
        // SPEC-157 — label renombrado "Crear" → "Crear Cotización"; path/ícono sin cambios.
        path: '/cotizaciones/nueva',
      },
      {
        text: 'Listar Cotizaciones',
        icon: <DraftsIcon />,
        // SPEC-116 v2.0 — navega directo al detalle de cotización de la bolsa
        // más reciente vía /cotizaciones/borradores (BorradoresRedirect),
        // reemplazando el destino intermedio de la v1.0 (rechazada en
        // certificación) y el de SPEC-102.
        // SPEC-121 — label renombrado de "Borradores" a "Bolsas"; path y
        // componente conservados sin cambios.
        // SPEC-157 — label renombrado "Bolsas" → "Listar Cotizaciones" y
        // reubicado al segundo lugar; path/ícono sin cambios.
        path: '/cotizaciones/borradores',
      },
      {
        text: 'Dashboard Cotizaciones',
        icon: <ListIcon />,
        // SPEC-157 — label renombrado "Cotizaciones" → "Dashboard Cotizaciones" y
        // reubicado al último lugar del submenú; path/ícono sin cambios.
        path: '/cotizaciones',
      },
    ],
  },
  {
    text: 'Contabilidad',
    icon: <ContabilidadIcon />,
    description: 'Módulo de contabilidad',
    subItems: [
      {
        text: 'Informe Bancario',
        icon: <InformeBancarioIcon />,
        path: '/contabilidad/comprobante-egreso',
      },
      {
        text: 'Comprobantes de Egresos',
        icon: <ComprobanteEgresoIcon />,
        path: '/contabilidad/comprobantes-egresos',
      },
      {
        text: 'Recibo de Caja',
        icon: <ReciboCajaIcon />,
        path: '/contabilidad/recibo-caja',
      },
    ],
  },
  {
    text: 'Configuración',
    icon: <SettingsIcon />,
    description: 'Configuración del sistema',
    path: '/configuracion',
  },
];

export const SideMenu: React.FC<SideMenuProps> = ({ onClose, onMenuClick }) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  // Función para cerrar todos los menús expandidos
  const closeAllMenus = () => {
    setExpandedItems([]);
  };

  // Effect para manejar clics fuera del menú
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeAllMenus();
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      // Si el foco se mueve fuera del menú, cerrar todos los submenús
      if (menuRef.current && !menuRef.current.contains(event.relatedTarget as Node)) {
        closeAllMenus();
      }
    };

    // Agregar event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusout', handleFocusOut);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const handleItemClick = (item: MenuItem) => {
    if (item.subItems && item.subItems.length > 0) {
      // Si tiene submenús, expandir/contraer
      setExpandedItems(prev => 
        prev.includes(item.text)
          ? prev.filter(name => name !== item.text)
          : [...prev, item.text]
      );
    } else if (item.path && onMenuClick) {
      // Si no tiene submenús y tiene path, navegar
      onMenuClick(item.path);
      onClose();
    }
  };

  const handleSubItemClick = (subItem: SubMenuItem) => {
    if (onMenuClick) {
      onMenuClick(subItem.path);
      closeAllMenus(); // Cerrar menús antes de cerrar el sidebar
      onClose();
    }
  };

  const isExpanded = (itemText: string) => expandedItems.includes(itemText);

  return (
    <Box 
      ref={menuRef}
      sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header del menú */}
      <Box
        sx={{
          height: '76px',
          backgroundColor: '#000000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingX: 2,
        }}
      >
        <Logo variant="black" height={40} />
        <IconButton
          onClick={onClose}
          sx={{
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Lista de menú */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ padding: 0 }}>
          {menuItems.map((item) => (
            <React.Fragment key={item.text}>
              {/* Elemento principal del menú */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  sx={{
                    paddingY: 2,
                    paddingX: 3,
                    '&:hover': {
                      backgroundColor: 'rgba(160, 88, 142, 0.1)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: '#a0588e',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 600,
                          color: '#000000',
                        }}
                      >
                        {item.text}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          color: '#666666',
                        }}
                      >
                        {item.description}
                      </Typography>
                    }
                  />
                  {item.subItems && item.subItems.length > 0 && (
                    <ListItemIcon sx={{ minWidth: 'auto', color: '#a0588e' }}>
                      {isExpanded(item.text) ? <ExpandLess /> : <ExpandMore />}
                    </ListItemIcon>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Submenús */}
              {item.subItems && item.subItems.length > 0 && (
                <Collapse in={isExpanded(item.text)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding>
                        <ListItemButton
                          onClick={() => handleSubItemClick(subItem)}
                          sx={{
                            paddingY: 1.5,
                            paddingX: 4,
                            paddingLeft: 6,
                            '&:hover': {
                              backgroundColor: 'rgba(160, 88, 142, 0.05)',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: '#a0588e',
                              minWidth: 30,
                            }}
                          >
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontFamily: 'Poppins, sans-serif',
                                  fontWeight: 500,
                                  color: '#333333',
                                  fontSize: '0.9rem',
                                }}
                              >
                                {subItem.text}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Footer del menú */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e0e0e0',
          padding: 2,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#666666',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          v{APP_VERSION} - Powered by CV Printing S.A.S
        </Typography>
      </Box>
    </Box>
  );
}; 